import { MenuItem, Typography, Popover, Select, Link, SelectProps } from "@mui/material";
import { Box } from "@mui/system";
import { GridRenderCellParams } from "@mui/x-data-grid-premium";
import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { DOCUMENT_ACCESS_OPTIONS } from "@shared/mirage/PickLists/DOCUMENT_ACCESS";

interface AccessCellProps extends GridRenderCellParams {
    onCellChanged: (id: number, field: string, value: string) => Promise<void>;
    readOnly?: boolean;
}

const AccessCell: React.FC<AccessCellProps> = ({ onCellChanged, readOnly, ...params }) => {
    const { id, field, value, colDef } = params;
    const [valueState, setValueState] = useState(value);
    const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
    const [openSelect, setOpenSelect] = useState(false);
    const wrapperRef = useRef<HTMLDivElement | null>();
    const handleChange = useCallback<NonNullable<SelectProps["onChange"]>>(
        async (event) => {
            const newValue = event.target.value as string;
            setValueState(newValue);
            setAnchorEl(null);
            await onCellChanged(Number(id), field as string, newValue as string);
        },
        [field, id, onCellChanged]
    );

    useEffect(() => {
        setValueState(value);
    }, [value]);

    const accessOnClick = useCallback((event: React.MouseEvent<HTMLAnchorElement, MouseEvent> | React.MouseEvent<HTMLSpanElement, MouseEvent>) => {
        event.stopPropagation();
        setAnchorEl(event.currentTarget);
        setOpenSelect((p) => !p);
    }, []);

    const onClose = () => {
        setOpenSelect(false);
    };

    const options = useMemo(() => {
        return DOCUMENT_ACCESS_OPTIONS.map((option) => {
            return (
                <MenuItem value={option} key={option}>
                    {option}
                </MenuItem>
            );
        });
    }, []);
    const readOnlyValue = <Typography variant="body2">{valueState}</Typography>;
    const editableValue = (
        <>
            <Link component="button" onClick={accessOnClick} variant="body2" underline="hover">
                {valueState}
            </Link>
            <Popover open={openSelect} anchorEl={anchorEl} onClose={onClose} sx={{ visibility: "hidden" }}>
                <Select
                    open={openSelect}
                    onClose={onClose}
                    value={valueState}
                    onChange={handleChange}
                    sx={{ minWidth: colDef.computedWidth }}
                    MenuProps={{
                        anchorEl,
                        anchorOrigin: { vertical: "top", horizontal: "left" },
                        transformOrigin: {
                            vertical: "top",
                            horizontal: "left",
                        },
                    }}
                >
                    {options}
                </Select>
            </Popover>
        </>
    );
    return (
        <Box component={"div"} ref={wrapperRef}>
            {readOnly ? readOnlyValue : editableValue}
        </Box>
    );
};

export default AccessCell;
